import { Component, ViewChild } from '@angular/core';
import {
  PoBreadcrumb,
  PoNotificationService,
  PoUploadComponent,
  PoUploadFileRestrictions
} from '@po-ui/ng-components';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { DpRubricasService } from './dp-rubricas.service';
import {
  ImportRubricaLinha,
  ImportRubricasResult,
  RubricaCadastroResult,
  rubricaJaCadastrada
} from './dp-rubricas.models';

type ArquivoUpload = { name: string; rawFile?: File };

@Component({
  selector: 'app-dp-rubricas',
  templateUrl: './dp-rubricas.component.html',
  styleUrls: ['./dp-rubricas.component.css'],
  standalone: false
})
export class DpRubricasComponent {
  @ViewChild('uploadPlanilha') uploadPlanilha?: PoUploadComponent;

  codiEmp: number | null = null;
  rubricaEsocial = '';
  rubricaDominio: number | null = null;
  descricao = '';

  salvando = false;
  importando = false;

  cadastro: RubricaCadastroResult | null = null;
  importacao: ImportRubricasResult | null = null;

  readonly urlPlaceholder = 'https://po-ui.io';
  readonly planilhaRestrictions: PoUploadFileRestrictions = {
    allowedExtensions: ['.xlsx', '.xlsm'],
    maxFileSize: 20 * 1024 * 1024
  };

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'DP' },
      { label: 'Cadastro de rubricas' }
    ]
  };

  constructor(
    private readonly rubricasService: DpRubricasService,
    private readonly poNotificationService: PoNotificationService,
    private readonly router: Router
  ) {}

  get linhasJaCadastradas(): ImportRubricaLinha[] {
    return (this.importacao?.linhas ?? []).filter(linha => rubricaJaCadastrada(linha.status));
  }

  jaCadastrada(status: string | null | undefined): boolean {
    return rubricaJaCadastrada(status);
  }

  irParaDescobrir(): void {
    void this.router.navigate(['/dp-descobrir-rubricas']);
  }

  cadastrar(): void {
    if (this.salvando) return;

    const codigo = this.rubricaEsocial.trim();
    if (!this.codiEmp || this.codiEmp <= 0) {
      this.poNotificationService.warning('Selecione a empresa.');
      return;
    }
    if (!codigo) {
      this.poNotificationService.warning('Informe a rubrica do eSocial.');
      return;
    }
    if (!this.rubricaDominio || this.rubricaDominio <= 0) {
      this.poNotificationService.warning('Informe a rubrica da Domínio.');
      return;
    }

    this.salvando = true;
    this.cadastro = null;
    this.rubricasService
      .cadastrar(this.codiEmp, codigo, this.rubricaDominio, this.descricao)
      .subscribe({
        next: result => {
          this.salvando = false;
          this.cadastro = result;
          this.avisarResultado(result.codiEmp, result.codRubrEsocial, result.status);
        },
        error: err => {
          this.salvando = false;
          this.poNotificationService.error(this.mensagemErro(err));
        }
      });
  }

  importarPlanilha(): void {
    if (this.importando) return;

    const arquivo = this.arquivos(this.uploadPlanilha)[0];
    if (!arquivo) {
      this.poNotificationService.warning('Selecione a planilha .xlsx de rubricas.');
      return;
    }

    this.importando = true;
    this.importacao = null;
    this.rubricasService.importarPlanilha(arquivo).subscribe({
      next: result => {
        this.importando = false;
        this.importacao = result;
        this.avisarImportacao(result);
        this.uploadPlanilha?.clear();
      },
      error: err => {
        this.importando = false;
        const corpo = err?.error as ImportRubricasResult | undefined;
        if (corpo?.linhas?.length) {
          this.importacao = corpo;
          this.avisarImportacao(corpo);
        }
        this.poNotificationService.error(this.mensagemErro(err));
      }
    });
  }

  classeStatus(status: string): string {
    if (rubricaJaCadastrada(status)) return 'partial';
    if (status.startsWith('Erro') || status.startsWith('Ignorada')) return 'blocked';
    if (status === 'Inserida') return 'done';
    return '';
  }

  private avisarResultado(codiEmp: number, codigo: string, status: string): void {
    if (rubricaJaCadastrada(status)) {
      this.poNotificationService.warning(
        `A empresa ${codiEmp} já tinha a rubrica ${codigo} cadastrada. ${status}`
      );
      return;
    }
    if (status === 'Inserida') {
      this.poNotificationService.success(`Rubrica ${codigo} cadastrada na empresa ${codiEmp}.`);
      return;
    }
    this.poNotificationService.success(status || 'Rubrica gravada.');
  }

  private avisarImportacao(result: ImportRubricasResult): void {
    const existentes = (result.linhas ?? []).filter(linha => rubricaJaCadastrada(linha.status));
    if (existentes.length > 0) {
      const empresas = [...new Set(existentes.map(linha => linha.codiEmp))];
      this.poNotificationService.warning(
        empresas.length === 1
          ? `A empresa ${empresas[0]} já tinha ${existentes.length} rubrica(s) cadastrada(s).`
          : `${existentes.length} rubrica(s) já estavam cadastradas nas empresas ${empresas.join(', ')}.`
      );
    }

    if (result.inseridas > 0) {
      this.poNotificationService.success(`${result.inseridas} rubrica(s) inserida(s).`);
    }
  }

  private arquivos(upload?: PoUploadComponent): File[] {
    const files = (upload?.currentFiles ?? []) as ArquivoUpload[];
    return files.map(item => item.rawFile).filter((file): file is File => !!file);
  }

  private mensagemErro(err: HttpErrorResponse | { error?: unknown }): string {
    const body = err?.error as { error?: string; Error?: string; title?: string } | string | undefined;
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      return body.error || body.Error || body.title || 'Não foi possível gravar as rubricas.';
    }
    return 'Não foi possível gravar as rubricas.';
  }
}
